import React, { useEffect, useMemo, useRef } from "react";
import { Modal, Input, Button, Spin, Tag, Popover, InputRef } from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  PlusOutlined,
  MinusOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { maskResidentName } from "../../../util/common";

interface ListData {
  data: AwaitableReturn<ISearchController["getSearch"]>["data"];
  isLoading: boolean;
}

interface SearchPatientModalProps {
  isOpen: "global" | "watchlist" | "bedExit" | null;
  onClose: () => void;

  searchValue: string;
  onChange: (value: string) => void;
  response: ListData;

  isInWatchlist: (residentId: number) => boolean;
  addToWatchlist: (residentId: number) => Promise<
    | {
        success: true;
        data: {
          data: IWatchlist[];
        };
      }
    | {
        success: boolean;
        data?: undefined;
      }
  >;
  removeFromWatchlist: (patientId: number) => Promise<void>;
  isAddingList: number[];
  onPatientClick: (patientId: number) => void;
  onSetupClick: (patientId: number) => void;
}

export const SearchPatientModal: React.FC<SearchPatientModalProps> = ({
  isOpen,
  onClose,
  searchValue,
  onChange,
  response,
  onPatientClick,
  isInWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  isAddingList,
  onSetupClick,
}) => {
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (inputRef.current && isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const sortedData = useMemo(() => {
    return response.data?.data.sort((a, b) => {
      return (a.resident.status ?? 0) - (b.resident.status ?? 0);
    });
  }, [response.data]);

  const handleSearch = (value: string) => {
    onChange(value);
  };

  const postTag = (item: ISearch) => {
    if (item.resident.status !== 1) {
      return (
        <Tag style={{ margin: 0 }}>
          {item.resident.status === 2 ? "Discharged" : "Leave"}
        </Tag>
      );
    }
    if (isOpen === "watchlist") {
      const isWatched = isInWatchlist(item.resident.id);
      return (
        <Button
          type="default"
          icon={
            isWatched ? (
              <Popover content="Remove from watchlist" placement="topLeft">
                <MinusOutlined style={{ color: "#000000" }} />
              </Popover>
            ) : (
              <Popover content="Add to watchlist" placement="topLeft">
                <PlusOutlined style={{ color: "#000000" }} />
              </Popover>
            )
          }
          loading={isAddingList.includes(item.resident.id)}
          disabled={isAddingList.length > 0}
          onClick={(e) => {
            e.stopPropagation();
            isWatched
              ? removeFromWatchlist(item.resident.id)
              : addToWatchlist(item.resident.id);
          }}
        />
      );
    }
    if (isOpen === "bedExit") {
      return (
        <Button
          type="default"
          onClick={(e) => {
            e.stopPropagation();
            onSetupClick(item.resident.id);
          }}
        >
          Setup
        </Button>
      );
    }
  };

  return (
    <Modal
      open={isOpen !== null}
      onCancel={onClose}
      footer={null}
      width={742}
      className={styles.searchModal}
      closeIcon={<CloseOutlined className={styles.closeIcon} />}
    >
      <div className={styles.searchContainer}>
        <Input
          placeholder="Search for Patient Name, Room"
          prefix={<SearchOutlined className={styles.searchIcon} />}
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          className={styles.searchInput}
          ref={inputRef}
        />
      </div>
      <div className={styles.patientList}>
        {response.isLoading ? (
          <Spin
            spinning={response.isLoading}
            className={styles.loading}
            indicator={
              <LoadingOutlined
                style={{ fontSize: 48, color: "#161646" }}
                spin
              />
            }
          />
        ) : (
          sortedData?.map((patient) => (
            <div
              key={patient.resident.id}
              className={classNames(styles.patientItem)}
              onClick={() => {
                onPatientClick(patient.resident.id);
              }}
            >
              <div className={styles.patientInfo}>
                <span className={styles.patientName}>
                  {maskResidentName(patient.resident.name)}
                </span>
                <span className={styles.patientCode}>
                  {patient.device.name}
                </span>
              </div>
              {postTag(patient)}
            </div>
          ))
        )}
      </div>
    </Modal>
  );
};
